import { RootState } from '@iwProvider/configureStore';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { InjectedFormProps, reduxForm } from 'redux-form';

import AddressDetails from './addressDetails/AddressDetails';
import ContactDetails from './contactDetails/ContactDetails';
import PersonDetails from './personDetails/PersonDetails';
import { DELETE_PERSON, PERSON_FORM_SUBMIT } from './PersonModal.actions';
import messages from './PersonModal.messages';
import styles from './PersonModal.module.css';
import { validate } from './PersonModalValidate';
import {
    Person as TDeserialisedPerson,
    getPersonFromUID,
    isDirector,
    isShareholder
} from '../../../api/lendingAPI/CustomerStateAPIHelper';
import { FormattedName } from '../../../iwIntlProvider/FormattedName';
import { CLOSE_MODAL } from '../../../iwModals/Modals.actions';
import promptMessages from '../../Profile/promptSave/PromptSave.messages';
import ConfirmationButton from '../../ui/confirmationButton/ConfirmationButton';
import { CancelAction } from '../../ui/confirmationResponse/confirmationQuestion/cancelAction/CancelAction';
import { ConfirmAction } from '../../ui/confirmationResponse/confirmationQuestion/confirmAction/ConfirmAction';
import { ConfirmationQuestion } from '../../ui/confirmationResponse/confirmationQuestion/ConfirmationQuestion';
import { ConfirmationResponse } from '../../ui/confirmationResponse/ConfirmationResponse';
import { ConfirmationStatement } from '../../ui/confirmationResponse/confirmationStatement/ConfirmationStatement';
import { Prompt } from '../../ui/prompt';

const formConfiguration = {
    form: 'person',
    validate: (
        values: TDeserialisedPerson & { ready: boolean },
        props: PropsFromRedux
    ) => props.personFormValidate(values)
};

const mapStateToProps = ({
    api,
    personModal
}: {
    api: RootState['api'];
    personModal: RootState['personModal'];
}) => {
    const person = getPersonFromUID(api.customer_state.data, personModal.uid!);
    return {
        ...personModal,
        initialValues: {
            ...personModal,
            isShareholder: isShareholder(person),
            isDirector: isDirector(person)
        },
        personFormValidate: (values: any) =>
            validate(api.customer_state.data.application.people, values)
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    closeModal: () => dispatch({ type: CLOSE_MODAL, id: 'PersonModal' }),
    submitPersonForm: (person: TDeserialisedPerson) =>
        dispatch({ type: PERSON_FORM_SUBMIT, person }),
    deletePerson: (uid: string) => dispatch({ type: DELETE_PERSON, uid })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;
const PersonModal = ({
    submitPersonForm,
    deletePerson,
    uid,
    isEditingPerson,
    firstName,
    lastName,
    isApplicant,
    isGuarantor,
    handleSubmit,
    personFormSubmitting,
    closeModal
}: PropsFromRedux & InjectedFormProps) => {
    const handleSubmitPersonForm = (person: TDeserialisedPerson) => {
        submitPersonForm(person);
    };

    const handleDeletePerson = () => {
        deletePerson(uid!);
    };

    const renderTitle = () => {
        if (!isEditingPerson) {
            return <FormattedMessage {...messages.addPerson} />;
        } else {
            const name = (
                <FormattedName
                    firstName={firstName}
                    lastName={lastName}
                    possessive={true}
                />
            );

            return (
                <FormattedMessage {...messages.editPerson} values={{ name }} />
            );
        }
    };

    const renderDeletePerson = () => {
        const name = (
            <FormattedName firstName={firstName} lastName={lastName} />
        );

        if (isApplicant || isGuarantor || !isEditingPerson) {
            return null;
        }

        return (
            <ConfirmationResponse
                className={styles.deletePerson}
                onSubmit={handleDeletePerson}
            >
                <ConfirmationStatement>
                    <FormattedMessage
                        {...messages.deletePerson}
                        values={{ name }}
                    />
                </ConfirmationStatement>

                <ConfirmationQuestion>
                    <FormattedMessage {...messages.deleteConfirmation} />

                    <ConfirmAction>
                        <FormattedMessage {...messages.confirmAction} />
                    </ConfirmAction>
                    <CancelAction>
                        <FormattedMessage {...messages.cancelAction} />
                    </CancelAction>
                </ConfirmationQuestion>
            </ConfirmationResponse>
        );
    };

    return (
        <div className={styles.PersonModal}>
            <div className={styles.title} data-testid="title">
                {renderTitle()}
                {renderDeletePerson()}
            </div>

            <form
                // @ts-expect-error Redux form being awkward. Can be properly typed when Redux Form is removed.
                onSubmit={handleSubmit(handleSubmitPersonForm)}
                autoComplete="off"
                data-testid="person-form"
            >
                <PersonDetails />

                <ContactDetails />

                <AddressDetails />

                <Prompt className={styles.prompt}>
                    <button onClick={closeModal}>
                        <FormattedMessage {...promptMessages.cancel} />
                    </button>
                    <ConfirmationButton
                        loading={personFormSubmitting}
                        type="submit"
                    >
                        <FormattedMessage {...promptMessages.save} />
                    </ConfirmationButton>
                </Prompt>
            </form>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(reduxForm<object, any>(formConfiguration)(PersonModal));
